// Outils partagés de l'application mobile (aucune dépendance en dehors d'axios).
import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const CODE_KEY = "khaldoun_code";
export const AGENT_KEY = "khaldoun_agent";
export const ROLE_KEY = "khaldoun_role";
// Agents : déconnexion après 15 min sans toucher l'écran (même règle vérifiée par le serveur).
export const INACTIVITE_MS = 15 * 60 * 1000;
export const MSG_INACTIVITE = "Déconnecté après 15 minutes d'inactivité. Reconnectez-vous.";

export const entetes = (code) => ({ "X-Access-Code": code });

// Contour de focus clavier commun (contraste suffisant sur fond clair et foncé)
export const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#14B8A6]";

export const lireStockage = (cle) => {
  try {
    return window.localStorage.getItem(cle) || "";
  } catch {
    return "";
  }
};

export const ecrireStockage = (cle, valeur) => {
  try {
    if (valeur) window.localStorage.setItem(cle, valeur);
    else window.localStorage.removeItem(cle);
  } catch {
    /* stockage indisponible (navigation privée) : la session ne sera pas mémorisée */
  }
};

export const estAnnulation = (err) => axios.isCancel(err) || err?.name === "CanceledError";

// Accès fermé par le serveur (hors horaires, logiciel du bureau fermé) : traité comme une fin de session,
// l'application revient à l'écran de connexion et y affiche le motif.
const PREFIXES_ACCES_FERME = ["Accès mobile possible seulement", "Accès autorisé uniquement"];
const PREFIXES_SESSION = ["Déconnecté après", "Session fermée"];
let motifDeconnexion = "";
export const definirMotifDeconnexion = (m) => {
  motifDeconnexion = m || "";
};
export const prendreMotifDeconnexion = () => {
  const m = motifDeconnexion;
  motifDeconnexion = "";
  return m;
};
export const estNonAutorise = (err) => {
  const statut = err?.response?.status;
  const d = err?.response?.data?.detail;
  if (statut === 401) {
    if (typeof d === "string" && PREFIXES_SESSION.some((p) => d.startsWith(p))) motifDeconnexion = d;
    return true;
  }
  if (statut === 403 && typeof d === "string" && PREFIXES_ACCES_FERME.some((p) => d.startsWith(p))) {
    motifDeconnexion = d;
    return true;
  }
  return false;
};

// Message à afficher : 403 et autres erreurs serveur -> le "detail" renvoyé par le serveur
// (ex. horaires d'accès) ; pas de réponse -> problème de réseau.
export function messageErreur(err, defaut = "Erreur de connexion. Réessayez.") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (!err?.response) return "Erreur de connexion. Réessayez.";
  return defaut;
}

// Numéro au format international pour WhatsApp (Algérie par défaut : 213)
export const numeroWhatsapp = (tel) => {
  let n = (tel || "").replace(/[^\d+]/g, "");
  if (n.startsWith("+")) n = n.slice(1);
  if (n.startsWith("00")) n = n.slice(2);
  else if (n.startsWith("0")) n = "213" + n.slice(1);
  return n;
};

export const lienTel = (tel) => `tel:${(tel || "").replace(/\s/g, "")}`;

// Redimensionne une image (max 1600 px) et renvoie un JPEG base64 (envoi léger)
export const redimensionnerImage = (file, maxDim = 1600, qualite = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", qualite));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const capagoIncomplete = (ref) => {
  const r = (ref || "").trim().toUpperCase();
  return !!r && !/^DZ[0-9A-Z]{6}$/.test(r);
};

export const nomComplet = (p) => `${p?.nom || ""} ${p?.prenom || ""}`.trim() || "—";

export const initiales = (p) =>
  `${(p?.nom || "").trim().charAt(0)}${(p?.prenom || "").trim().charAt(0)}`.toUpperCase() || "?";

// RDV détecté par le bot Capago mais date pas encore saisie par l'agence
export const rdvBotASaisir = (p) => !!(p?.rdv_bot || "").trim() && !(p?.date_rdv || "").trim();

// ---------------------------------------------------------------------------
// Dates de RDV « JJ/MM/AAAA HH:MM », heure d'Algérie (UTC+1, sans heure d'été).
// Sans heure : 09:00 (même règle que le serveur).
// ---------------------------------------------------------------------------
const RE_RDV = /(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/;
const JOURS = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];
const deux = (n) => String(n).padStart(2, "0");

export function analyserRdv(texte) {
  const m = RE_RDV.exec(String(texte || ""));
  if (!m) return null;
  const j = +m[1];
  const mo = +m[2];
  const a = +m[3];
  const h = m[4] ? +m[4] : 9;
  const mi = m[5] ? +m[5] : 0;
  if (mo < 1 || mo > 12 || j < 1 || j > 31 || h > 23 || mi > 59) return null;
  return {
    j,
    mo,
    a,
    h,
    mi,
    avecHeure: !!m[4],
    instant: Date.UTC(a, mo - 1, j, h - 1, mi), // heure d'Algérie -> instant absolu
    jourSemaine: new Date(Date.UTC(a, mo - 1, j)).getUTCDay(),
  };
}

export const jourCourt = (r) => JOURS[r.jourSemaine];
export const dateCourte = (r) => `${deux(r.j)}/${deux(r.mo)}`;
export const heureRdv = (r) => (r.avecHeure ? `${deux(r.h)}:${deux(r.mi)}` : "");

// « Dans 18 h », « Dans 1 j 6 h »… ; urgent = moins de 24 h
export function compteARebours(r, maintenant) {
  const minutes = Math.floor((r.instant - maintenant) / 60000);
  if (minutes < 0) return { texte: "Date passée", urgent: false, passe: true, proche: false };
  const heures = Math.floor(minutes / 60);
  let texte;
  if (heures < 1) texte = `Dans ${minutes} min`;
  else if (heures < 24) texte = `Dans ${heures} h`;
  else {
    const jours = Math.floor(heures / 24);
    const reste = heures % 24;
    texte = reste ? `Dans ${jours} j ${reste} h` : `Dans ${jours} j`;
  }
  return { texte, urgent: heures < 24, passe: false, proche: minutes <= 72 * 60 };
}

// Résumé court d'un RDV : « RDV 22/10 · 09:30 »
export function resumeRdv(dateRdv) {
  const r = analyserRdv(dateRdv);
  if (!r) return dateRdv ? `RDV ${dateRdv}` : "";
  const h = heureRdv(r);
  return `RDV ${dateCourte(r)}${h ? ` · ${h}` : ""}`;
}

// Horodatage des scans « AAAA-MM-JJTHH:MM:SS » (heure d'Algérie) -> « il y a 12 min »
export function ilYA(creeLe, maintenant) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(creeLe || "");
  if (!m) return "";
  const instant = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4] - 1, +m[5]);
  const minutes = Math.max(0, Math.floor((maintenant - instant) / 60000));
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.floor(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  return `${m[3]}/${m[2]} ${m[4]}:${m[5]}`;
}

// ---------------------------------------------------------------------------
// Couleurs (palette validée : slate / teal). Contrastes texte/fond >= 4,5:1.
// ---------------------------------------------------------------------------
export const COULEUR_AVATAR = {
  rdv: "#0F766E",
  attente: "#B45309",
  doublon: "#B91C1C",
  autre: "#475569",
};

// Catégorie d'une personne d'après les indicateurs calculés par le serveur
export function categorie(p) {
  const f = p?.filtres || {};
  if (f.doublon) return "doublon";
  if (f.rdv) return "rdv";
  if (f.attente) return "attente";
  return "autre";
}

export function styleStatut(statut, cat) {
  const v = (statut || "").toLowerCase();
  if (v.includes("doublon")) return { background: "#FEE2E2", color: "#B91C1C" };
  if (v.includes("refus")) return { background: "#FEE2E2", color: "#B91C1C" };
  if (v.includes("approuv")) return { background: "#D1FAE5", color: "#065F46" };
  if (v.includes("dépos") || v.includes("depos")) return { background: "#DBEAFE", color: "#1E40AF" };
  if (v.includes("rendez") || cat === "rdv") return { background: "#CCFBF1", color: "#115E59" };
  if (v.includes("attente") || cat === "attente") return { background: "#FEF3C7", color: "#92400E" };
  return { background: "#E2E8F0", color: "#334155" };
}

export function libelleStatut(p, cat) {
  if ((p?.statut || "").trim()) return p.statut.trim();
  if (cat === "rdv") return "RDV pris";
  if (cat === "attente") return "En attente";
  if (cat === "doublon") return "Doublon";
  return "";
}

export function stylePaiement(s) {
  const v = (s || "").toLowerCase();
  if (v.includes("total")) return { background: "#D1FAE5", color: "#065F46" };
  if (v.includes("acompte")) return { background: "#FEF3C7", color: "#92400E" };
  return { background: "#FEE2E2", color: "#B91C1C" };
}

const FORMAT_DZD = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
export const montant = (n) => `${FORMAT_DZD.format(Number(n) || 0)} DZD`;

// ---------------------------------------------------------------------------
// Plein écran + paysage pour la signature (Android). Sur iPhone ces API n'existent
// pas ou refusent : l'écran de signature fait alors pivoter son contenu en CSS.
// À appeler DIRECTEMENT dans le gestionnaire du clic (geste utilisateur requis).
// ---------------------------------------------------------------------------
export function entrerPleinEcranPaysage() {
  let attente = Promise.resolve();
  try {
    const el = document.documentElement;
    const demander = el.requestFullscreen || el.webkitRequestFullscreen;
    if (demander && !document.fullscreenElement && !document.webkitFullscreenElement) {
      attente = Promise.resolve(demander.call(el, { navigationUI: "hide" }));
    }
  } catch {
    /* plein écran refusé */
  }
  return attente
    .catch(() => {})
    .then(() => {
      try {
        const o = window.screen && window.screen.orientation;
        if (o && typeof o.lock === "function") return o.lock("landscape").catch(() => {});
      } catch {
        /* verrouillage non supporté */
      }
      return undefined;
    });
}

export function quitterPleinEcran() {
  try {
    const o = window.screen && window.screen.orientation;
    if (o && typeof o.unlock === "function") o.unlock();
  } catch {
    /* rien à déverrouiller */
  }
  try {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  } catch {
    /* déjà sorti */
  }
}
